import React from 'react';
import classNames from 'classnames';

import { isUserIdentityVerified } from '../../util/identity';
import { listeningOfferFromListing } from '../../util/sweetSkills';

import css from './ListenerPriceTiles.module.css';
import { useHidePrices } from '../FirstSessionFree/FirstSessionFree';

/**
 * Format tiles + "Starting at" line (Sam, Oct 18 card mockup).
 * Prices are the listener's own per-15-minute rates (FORMAT_TILES is only the fallback).
 */
export const SESSION_MINUTES = 15;
export const FORMAT_TILES = [
  { id: 'chat', label: 'Text chat', price: 10 },
  { id: 'voice', label: 'Voice call', price: 20 },
  { id: 'video', label: 'Video call', price: 30 },
];

/**
 * Only the formats the listener offers (all three when none are saved yet).
 *
 * @param {Object} [publicData]
 * @returns {{ from: number, tiles: Array<{id:string,label:string,price:number}> }}
 */
export const listenerPricing = publicData => {
  const offered = Array.isArray(publicData?.offeredFormats) ? publicData.offeredFormats : [];
  const rates = listeningOfferFromListing({ attributes: { publicData: publicData || {} } }).durations['15'] || {};
  const priced = FORMAT_TILES.map(t => (Number.isFinite(rates[t.id]) ? { ...t, price: rates[t.id] } : t));
  const hasOwn = Array.isArray(publicData?.skillOffers);
  const picked = priced.filter(t => (hasOwn ? Number.isFinite(rates[t.id]) : offered.includes(t.id)));
  const tiles = picked.length ? picked : priced;
  return { from: Math.min(...tiles.map(t => t.price)), tiles };
};

const Icon = ({ id }) => {
  if (id === 'voice') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          fill="currentColor"
          d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.3 0 .7-.2 1l-2.3 2.2z"
        />
      </svg>
    );
  }
  if (id === 'video') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          fill="currentColor"
          d="M4 6h11a2 2 0 0 1 2 2v1.5l3.4-2.3c.7-.4 1.6.1 1.6.9v7.8c0 .8-.9 1.3-1.6.9L17 14.5V16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8c0-1.1.9-2 2-2z"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-4.4 3.5c-.6.5-1.6 0-1.6-.8V18a2 2 0 0 1-1-1.7V6c0-1.1.9-2 2-2z" />
      <circle cx="7.5" cy="11" r="1.4" fill="#fff" />
      <circle cx="12" cy="11" r="1.4" fill="#fff" />
      <circle cx="16.5" cy="11" r="1.4" fill="#fff" />
    </svg>
  );
};

export const StartingAtLine = ({ publicData, className, small = false, plain = false }) => {
  const { from } = listenerPricing(publicData);
  const free = useHidePrices();
  if (free) return <p className={classNames(css.startingAt, { [css.startingSmall]: small, [css.startingPlain]: plain }, className)} data-starting-at="free"><strong className={css.startingPrice}>First {SESSION_MINUTES} min FREE</strong></p>;
  return (
    <p className={classNames(css.startingAt, { [css.startingSmall]: small, [css.startingPlain]: plain }, className)} data-starting-at={from}>
      From <strong className={css.startingPrice}>${from}</strong> per {SESSION_MINUTES} min
    </p>
  );
};

/**
 * @param {Object} props
 * @param {Object} props.publicData
 * @param {boolean} [props.showHeading]
 * @param {boolean} [props.compact] smaller tiles for narrow cards / the sidebar
 */
const ListenerPriceTiles = props => {
  const { publicData, showHeading = true, compact = false, className } = props;
  const { tiles } = listenerPricing(publicData);
  const free = useHidePrices();
  return (
    <div className={classNames(css.root, { [css.compact]: compact }, className)} data-format-tiles="true">
      {showHeading ? <p className={css.heading}>Choose how you’d like to talk</p> : null}
      <ul className={css.tiles} style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}>
        {tiles.map(t => (
          <li key={t.id} className={classNames(css.tile, css[`tile_${t.id}`])} data-format-tile={t.id}>
            <span className={css.icon}>
              <Icon id={t.id} />
            </span>
            <span className={css.tileLabel}>{t.label}</span>
            <span className={css.tileMeta}>
              {free ? <span className={css.tilePrice}>FREE first {SESSION_MINUTES} min</span> : <><span className={css.tilePrice}>${t.price}</span> per {SESSION_MINUTES} min</>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * "ID-verified adult · Conversations are never recorded" (verified part only
 * when the listener's ID check passed).
 */
export const ListenerTrustLine = ({ user, className }) => {
  const verified = isUserIdentityVerified(user);
  return (
    <div className={classNames(css.trustWrap, className)}>
      <p className={css.trust} data-listener-trust-line="true">
        {verified ? (
          <span className={css.trustItem}>
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={css.shield}>
              <path
                fill="currentColor"
                d="M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3zm-1.2 14.2-3.5-3.5 1.4-1.4 2.1 2.1 4.9-4.9 1.4 1.4-6.3 6.3z"
              />
            </svg>
            ID-verified adult
            <span className={css.trustDot} aria-hidden="true">
              ·
            </span>
          </span>
        ) : null}
        <span className={css.trustItem}>Conversations are never recorded</span>
      </p>
    </div>
  );
};

export default ListenerPriceTiles;
